import React from 'react';
import { Link } from 'react-router-dom';
import { getPartnerServiceDeskUrlForGenericHelp, trackPartnerServiceDeskClick } from '@css-xp/ppc-navigation';
import { renderTableActions } from '@css-xp/base';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { emptyStateImgLink, discountEmptyStateImg, quoteEmptyStateImg, paymentMethodEmptyImg } from './constant';
import {
  EmptyStateAddTeamIcon,
  EmptyStateAddCardIcon,
  ErrorNoResultEmptyStateIcon,
  ErrorSearchEmptyStateIcon,
  EmptyStateAddUserIcon,
} from './empty-state-icons';

export const getSupportUrl = () => {
  return (
    <a
      data-testId="service-desk-link"
      href={getPartnerServiceDeskUrlForGenericHelp()}
      target="_blank"
      rel="noreferrer"
      onClick={trackPartnerServiceDeskClick}
    >
      Partner Service Desk
    </a>
  );
};

export const failureMessage = () => {
  return <div data-testId="failure-message">Try again or contact {getSupportUrl()}.</div>;
};

export const tableErrorMessage = () => {
  return <div data-testId="table-error-message">Try refreshing the page or contacting {getSupportUrl()}.</div>;
};

export const errorStateProp = {
  header: "We can't show these details right now",
  description: tableErrorMessage(),
};

export const customEmptyStateProp = (actions: React.ElementType<any>[] | undefined) => {
  return {
    header: '',
    description: 'No details to show',
    imageUrl: emptyStateImgLink,
    primaryAction: actions ? renderTableActions(actions) : undefined,
  };
};

export const getBillingProfileUrl = (name: string, id: string, txaId: string) => {
  return (
    <Link
      to={`${getRouteUrl(routePaths.billingProfileDetails, {
        txaId: txaId,
        billingProfileId: id,
      })}`}
      onClick={(e) => e.stopPropagation()}
    >
      {name}
    </Link>
  );
};

export const emptyStateSearchTabs = {
  header: '',
  description: 'No matching results',
  imageUrl: emptyStateImgLink,
};

export const emptyStatePropDiscountTable = {
  header: 'No discount to display yet',
  description: '',
  imageUrl: discountEmptyStateImg,
};

export const emptyStatePropDiscountTableDesignIteration1 = {
  header: 'See user discounts in one place',
  description: "You'll see a list of any discounts here, plus some of the main details.",
  imageUrl: discountEmptyStateImg,
};

export const emptyStateWithDescriptionProp = {
  header: 'No details to show',
  description: "Try adjusting your search or filter to find what you're looking for",
  imageUrl: emptyStateImgLink,
};

/* use this for empty state in case there is no specific empty state available
this is equivalent of emptyStateProp for designIteration */
export const genericEmptyStateProp = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'No details to show',
};

export const emptyStatePropForWrongSearch = {
  renderImage: ErrorSearchEmptyStateIcon,
  header: "We couldn't find any matches for your search",
  description: 'Check for typos or try different keywords.',
};

export const emptyStatePropForPaymentMethods = (secondaryAction: React.ReactNode, showDefault = true) => {
  if (showDefault) {
    return {
      header: 'No payment method set yet',
      description: 'Add your first debit/credit card by clicking the button below',
      imageUrl: paymentMethodEmptyImg,
      secondaryAction: secondaryAction,
    };
  }

  return {
    renderImage: EmptyStateAddCardIcon,
    header: 'Manage payment methods in one place',
    description: 'Add a credit or debit card and set as the default payment method.',
    secondaryAction: secondaryAction,
  };
};

export const emptyStatePropForDiscountTab = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Discounts will display here',
  description: "See a breakdown of the discount information applied to your customer's subscriptions.",
};

export const emptyStatePropForAuditNotesTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Stay up-to-date with billing information',
  description: "We'll show you any action that's been taken on your customer's billing account here.",
};

export const emptyStatePropForEmailTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Access subscription emails in one place',
  description: "When you have any emails, we'll display them here.",
};

export const emptyStatePropForOrderHistoryTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Keep track of orders',
  description: "When your customers have any orders, you'll be able to view and download them here.",
};

export const emptyStatePropForOrderHistoryTabOnDetailPage = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Keep track of the order history',
  description: "We'll let you know the details of all your customer's orders here.",
};

export const emptyStatePropForSoldToAddressTable = (secondaryActions: React.ElementType<any>[]) => ({
  renderImage: EmptyStateAddTeamIcon,
  header: "Here's where you'll find your customers",
  description: 'Any customers you add will appear here.',
  secondaryAction: renderTableActions(secondaryActions),
});

export const emptyStatePropForInvoiceTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Keep track of invoices',
  description: "When your customers have any invoices, you'll be able to view and download them here.",
};

export const emptyStatePropForQuoteTable = (secondaryAction: React.ReactNode) => ({
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'See quotes in one place',
  description: "You'll see a list of any quotes here, plus some of the main details.",
  secondaryAction: secondaryAction,
});

export const emptyStatePropForSubscriptionTableFlat = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'See subscriptions in one place',
  description: "You'll see a list of any subscriptions here, plus some of the main details.",
};

export const emptyStatePropForInvoiceContactsTab = (secondaryActions: React.ElementType<any>[]) => ({
  renderImage: EmptyStateAddUserIcon,
  header: "Here's where you'll find your invoice contacts",
  description: 'Any contacts you add will appear here.',
  secondaryAction: renderTableActions(secondaryActions),
});

export const getEmptyStatePropForBillingProfileTable = (primaryAction?: React.ReactNode) => {
  return {
    renderImage: ErrorNoResultEmptyStateIcon,
    header: 'View your billing profile details here',
    description: 'See an overview of all the main information for each billing profile.',
    primaryAction: primaryAction,
  };
};

export const emptyStatePropForCustomerContactsTab = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'View your customer contacts here',
  description: 'Keep track of everyone you manage subscriptions with on this billing profile.',
};

export const emptyStatePropForIULContactsTab = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'View key and other contacts here',
  description: "We'll display your contacts for this billing profile.",
};

export const emptyStatePropForAdminsTab = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'View billing admins here',
  description: "We'll display a list of admins who manage billing details and permissions.",
};

export const emptyStatePropForBillingProfilesBillingAdminsTab = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'View billing admins here',
  description: "We'll display a list of admins with this billing profile.",
};

export const DEFAULT_INFLIGHT_BANNER_MESSAGE =
  'An invoice is currently being generated. Any changes you make now will appear on this invoice.';

export const getEmptyStatePropQuoteTable = (primaryAction?: React.ReactNode, hasFilter?: boolean) => {
  return {
    header: hasFilter ? "We couldn't find any results for this. Try again" : 'No quotes yet',
    description: '',
    imageUrl: quoteEmptyStateImg,
    primaryAction: primaryAction,
  };
};

export const EditSoldToAddressDisabledMessage = () => (
  <div data-testid="edit-sold-to-address-disabled-message">
    This sold-to address cannot be edited due to the price eligibility applied to it. For more information please reach
    out to Partner Service Desk.
  </div>
);

export const searchPageInitialEmptyState = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Find what you need in Partner Purchasing Center',
  description: 'Enter a name, number or other keywords in search',
};

export const recentlyViewedDrawerInitialState = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Begin searching',
  description: 'Your recent searches will appear here',
};

export const emptyStatePropForUserHistoryTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'See user history in one place',
  description: "You'll see a list of any user history here, plus some of the main details.",
};

export const emptyStatePropForRefundsTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'Keep track of refunds',
  description: 'When your customers have any refunds, you’ll be able to view and download them here.',
};

export const emptyStatePropForAppsTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: 'See apps in one place',
  description: "You'll see a list of apps here, plus some of the main details.",
};

export const emptyStatePropForAddAppToProductFlow = {
  renderImage: ErrorSearchEmptyStateIcon,
  header: 'Search for app',
  description: 'Type the name of a marketplace app',
};

export const emptyStatePropForAddAppOrAddOnsToProductFlow = {
  renderImage: ErrorSearchEmptyStateIcon,
  header: 'Search for app or add-on',
  description: 'Type the name of a marketplace app or add-on',
};

export const emptyStatePropForAddMarketplaceAppOrAddOns = {
  renderImage: ErrorSearchEmptyStateIcon,
  header: 'Your Marketplace apps and add-ons will appear here',
  description: 'Enter the name of a Marketplace app or add-on to select items.',
};

export const emptyStatePropForAddOnsToProductFlow = {
  renderImage: ErrorSearchEmptyStateIcon,
  header: 'Search for add-on',
  description: 'Type the name of an add-on',
};

export const emptyStatePropForAccountBalanceTable = {
  renderImage: ErrorNoResultEmptyStateIcon,
  header: "You don't have any transactions",
};

export const emptyStatePropForPricingPlanTable = {
  imageUrl: emptyStateImgLink,
  header: 'No pricing plans found',
};
